if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        const quantityInput = this.form.querySelector('[name="quantity"]');
        if (theme.settings.afterAtc !== 'no-js'
          || Number(quantityInput?.dataset.quantityMin) > 1
          || Number(quantityInput?.getAttribute('step')) > 1
          || quantityInput?.hasAttribute('data-inventory-quantity')) {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const quantityInput = this.form.querySelector('[name="quantity"]');
      quantityInput?.closest('quantity-input')?.normalize();
      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };
      let added = false;
      let addRequested = false;

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();
        customElements.get('quantity-input')?.syncCart(oldCartData);

        const step = Number(quantityInput?.getAttribute('step')) || 1;
        const minimum = Math.ceil((Number(quantityInput?.dataset.quantityMin) || 1) / step) * step;
        const limitedStock = quantityInput?.hasAttribute('data-inventory-quantity');
        if (minimum > 1 || step > 1 || limitedStock) {
          const variantId = Number(formData.get('id'));
          const existingQuantity = oldCartData.items.reduce((total, item) => (
            item.variant_id === variantId ? total + item.quantity : total
          ), 0);
          const requested = Math.max(1, Number(formData.get('quantity')) || minimum);
          let total = Math.max(minimum, Math.ceil((existingQuantity + requested) / step) * step);
          if (limitedStock) {
            const maximum = Math.floor(Number(quantityInput.dataset.inventoryQuantity) / step) * step;
            const remaining = Math.floor(Math.max(0, maximum - existingQuantity) / step) * step;
            if (remaining < minimum) {
              this.setErrorMsgState('No hay stock suficiente para agregar otra cantidad que cumpla el mínimo y el volumen de compra.');
              return;
            }
            total = Math.min(total, maximum);
          }
          formData.set('quantity', total - existingQuantity);
          if (quantityInput) quantityInput.value = total - existingQuantity;
          quantityInput?.closest('quantity-input')?.normalize();
        }

        addRequested = true;
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);
        added = true;
        customElements.get('quantity-input')?.syncCart({
          items: [...oldCartData.items.filter((item) => item.key !== data.key), data]
        });

        if (theme.settings.afterAtc === 'page' || theme.settings.afterAtc === 'no-js') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          const sectionIds = sections.split(',');
          const missingSections = sectionIds.filter((id) => typeof data.sections?.[id] !== 'string');
          if (missingSections.length) {
            const url = new URL(window.location.href);
            url.searchParams.delete('section_id');
            url.searchParams.set('sections', missingSections.join(','));
            const sectionsResponse = await fetch(url);
            if (!sectionsResponse.ok) throw new Error(sectionsResponse.status);
            data.sections = { ...(data.sections || {}), ...(await sectionsResponse.json()) };
          }
          if (sectionIds.some((id) => typeof data.sections?.[id] !== 'string')) {
            throw new Error('No se pudo actualizar la vista del carrito.');
          }
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        if (added) {
          this.setErrorMsgState('El producto se agregó, pero no se pudo actualizar la vista del carrito. Recarga la página para ver el carrito actualizado.');
        }
        if (addRequested) {
          try {
            const cartResponse = await fetch(`${theme.routes.cart}.js`);
            if (cartResponse.ok) customElements.get('quantity-input')?.syncCart(await cartResponse.json());
          } catch (cartError) {
            // Keep the original cart error if refreshing quantities also fails.
          }
        }
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        if (added) this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
